import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx-js-style";
import {
  CalendarRange,
  CircleDollarSign,
  FileSpreadsheet,
  Filter,
  LineChart as LineChartIcon,
  PieChart as PieChartIcon,
  Plus,
  Printer,
  Receipt,
  RefreshCw,
  Search,
  Trash2,
  Eye,
  Pencil,
  Download,
  Paperclip,
  Building2,
  Wallet,
  X,
  Loader2
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  XAxis,
  YAxis
} from "recharts";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface ExpenseCategory {
  id: number;
  name: string;
  description?: string | null;
  tenantId?: number | null;
  isDefault?: boolean;
}

interface ExpenseRow {
  id: number;
  tenantId?: number | null;
  schoolName?: string | null;
  transactionNumber: string;
  receiptNumber?: string | null;
  receiptDate: string;
  vendorName: string;
  description: string;
  categoryName?: string | null;
  amount: number;
  paymentMethod?: string | null;
  status: "active" | "archived";
  recordedByName?: string | null;
  createdAt?: string;
  updatedAt?: string;
  attachmentCount?: number;
}

interface ExpenseAttachment {
  id: number;
  fileName: string;
  mimeType?: string | null;
  fileData?: string | null;
  createdAt?: string;
}

interface ExpenseDetail extends ExpenseRow {
  categoryId?: number | null;
  recordedBy?: number | null;
  attachments?: ExpenseAttachment[];
}

interface SummaryResponse {
  summary: {
    totalThisMonth: number;
    totalLastThreeMonths: number;
    totalRecords: number;
    highestCategory: string;
  };
  reports: {
    monthlyTrend: Array<{ month: string; total: number }>;
    categoryBreakdown: Array<{ categoryName: string; total: number }>;
    topVendors: Array<{ vendorName: string; total: number }>;
    schoolBreakdown?: Array<{ tenantId: number; schoolName: string; total: number }>;
  };
}

interface SchoolOption {
  id: number;
  name: string;
  subdomain?: string | null;
}

interface FormState {
  id?: number;
  transactionNumber: string;
  receiptNumber: string;
  receiptDate: string;
  vendorName: string;
  categoryId: string;
  description: string;
  amount: string;
  paymentMethod: string;
  status: "active" | "archived";
}

interface AttachmentPayload {
  fileName: string;
  mimeType: string;
  fileData: string;
}

const PAYMENT_METHODS = ["Cash", "Bank Transfer", "Mobile Money", "Cheque", "Card", "Other"];
const REPORT_COLORS = ["#0d9488", "#0ea5e9", "#f59e0b", "#6366f1", "#ef4444", "#22c55e", "#14b8a6"];

const buildDefaultRange = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 3);
  return {
    startDate: start.toISOString().split("T")[0],
    endDate: end.toISOString().split("T")[0]
  };
};

const initialFormState = (): FormState => ({
  transactionNumber: "",
  receiptNumber: "",
  receiptDate: new Date().toISOString().split("T")[0],
  vendorName: "",
  categoryId: "",
  description: "",
  amount: "",
  paymentMethod: "Cash",
  status: "active"
});

const SchoolExpenses: React.FC = () => {
  const token = localStorage.getItem("token") || "";
  const currentUser = localStorage.getItem("userName") || "Current User";
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isSuperAdmin = role === "superadmin";

  const defaultRange = useMemo(() => buildDefaultRange(), []);
  const [search, setSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [schools, setSchools] = useState<SchoolOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>("all");

  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [expenses, setExpenses] = useState<ExpenseRow[]>([]);
  const [summary, setSummary] = useState<SummaryResponse | null>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<ExpenseDetail | null>(null);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [attachment, setAttachment] = useState<AttachmentPayload | null>(null);
  const [categoryName, setCategoryName] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  const [isCreatingCategory, setIsCreatingCategory] = useState(false);

  useAutoDismiss(message, setMessage, null, 5000);
  useAutoDismiss(error, setError, null, 5000);

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const selectedTenantScope = useMemo(() => {
    if (!isSuperAdmin || selectedTenantId === "all") return null;
    const value = Number(selectedTenantId);
    return Number.isFinite(value) ? value : null;
  }, [isSuperAdmin, selectedTenantId]);
  const isAllSchoolsMode = isSuperAdmin && !selectedTenantScope;

  const formatCurrency = (value: number | undefined) =>
    new Intl.NumberFormat("en-ZM", {
      style: "currency",
      currency: "ZMW",
      maximumFractionDigits: 2
    }).format(Number(value || 0));

  const formatDate = (value?: string | null) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-ZM", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const getQueryParams = useCallback(
    () => ({
      search: search.trim() || undefined,
      vendor: vendorFilter.trim() || undefined,
      categoryId: !isAllSchoolsMode ? categoryFilter || undefined : undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      includeArchived: includeArchived || undefined,
      tenantId: selectedTenantScope || undefined
    }),
    [search, vendorFilter, categoryFilter, startDate, endDate, includeArchived, isAllSchoolsMode, selectedTenantScope]
  );

  const fetchSchools = useCallback(async () => {
    if (!token || !isSuperAdmin) return;
    const res = await axios.get<SchoolOption[]>(getApiUrl("superadmin/schools"), authHeaders);
    const nextSchools = Array.isArray(res.data) ? res.data : [];
    setSchools(nextSchools.map((school) => ({ id: Number(school.id), name: school.name, subdomain: school.subdomain })));
  }, [token, isSuperAdmin, authHeaders]);

  const fetchCategories = useCallback(async () => {
    if (!token) return;
    if (isAllSchoolsMode) {
      setCategories([]);
      return;
    }
    const res = await axios.get<{ categories: ExpenseCategory[] }>(getApiUrl("expenses/categories"), {
      ...authHeaders,
      params: selectedTenantScope ? { tenantId: selectedTenantScope } : undefined
    });
    setCategories(res.data.categories || []);
  }, [token, authHeaders, isAllSchoolsMode, selectedTenantScope]);

  const fetchExpenses = useCallback(async () => {
    if (!token) return;
    const res = await axios.get<{ expenses: ExpenseRow[] }>(getApiUrl("expenses"), {
      ...authHeaders,
      params: getQueryParams()
    });
    setExpenses((res.data.expenses || []).map((expense) => ({ ...expense, amount: Number(expense.amount || 0) })));
  }, [token, authHeaders, getQueryParams]);

  const fetchSummary = useCallback(async () => {
    if (!token) return;
    const res = await axios.get<SummaryResponse>(getApiUrl("expenses/summary"), {
      ...authHeaders,
      params: getQueryParams()
    });
    setSummary(res.data);
  }, [token, authHeaders, getQueryParams]);

  const refreshData = useCallback(async () => {
    if (!token) {
      setError("You are not signed in. Please log in again.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await Promise.all([isSuperAdmin ? fetchSchools() : Promise.resolve(), fetchCategories(), fetchExpenses(), fetchSummary()]);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load school expenses.");
    } finally {
      setLoading(false);
    }
  }, [token, isSuperAdmin, fetchSchools, fetchCategories, fetchExpenses, fetchSummary]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const resetFilters = () => {
    setSearch("");
    setVendorFilter("");
    setCategoryFilter("");
    setStartDate(defaultRange.startDate);
    setEndDate(defaultRange.endDate);
    setIncludeArchived(false);
    if (isSuperAdmin) {
      setSelectedTenantId("all");
    }
  };

  const resetForm = () => {
    setFormState(initialFormState());
    setAttachment(null);
    setCategoryName("");
    setCategoryDescription("");
    setIsCreatingCategory(false);
  };

  const openCreateModal = () => {
    if (isSuperAdmin) return;
    resetForm();
    setIsFormOpen(true);
  };

  const getScopedParams = useCallback(
    (tenantIdOverride?: number | null) => {
      const tenantId = tenantIdOverride || selectedTenantScope;
      return tenantId ? { tenantId } : undefined;
    },
    [selectedTenantScope]
  );

  const openEditModal = async (expenseId: number) => {
    if (isSuperAdmin) return;
    setDetailLoading(true);
    try {
      const expenseRow = expenses.find((item) => item.id === expenseId);
      const res = await axios.get<{ expense: ExpenseDetail }>(getApiUrl(`expenses/${expenseId}`), {
        ...authHeaders,
        params: getScopedParams(expenseRow?.tenantId)
      });
      const expense = res.data.expense;
      setIsDetailOpen(false);
      setFormState({
        id: expense.id,
        transactionNumber: expense.transactionNumber || "",
        receiptNumber: expense.receiptNumber || "",
        receiptDate: expense.receiptDate ? expense.receiptDate.split("T")[0] : "",
        vendorName: expense.vendorName || "",
        categoryId: expense.categoryId ? String(expense.categoryId) : "",
        description: expense.description || "",
        amount: String(expense.amount || ""),
        paymentMethod: expense.paymentMethod || "Cash",
        status: expense.status || "active"
      });
      setAttachment(null);
      setIsFormOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load expense details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const openDetailModal = async (expenseId: number) => {
    setDetailLoading(true);
    try {
      const expenseRow = expenses.find((item) => item.id === expenseId);
      const res = await axios.get<{ expense: ExpenseDetail }>(getApiUrl(`expenses/${expenseId}`), {
        ...authHeaders,
        params: getScopedParams(expenseRow?.tenantId)
      });
      setSelectedExpense(res.data.expense);
      setIsDetailOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load expense details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleFormChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const fileToBase64 = (file: File) =>
    new Promise<AttachmentPayload>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve({
          fileName: file.name,
          mimeType: file.type || "application/octet-stream",
          fileData: String(reader.result || "")
        });
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleAttachmentChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      setAttachment(null);
      return;
    }

    try {
      const payload = await fileToBase64(file);
      setAttachment(payload);
    } catch {
      setError("Failed to process the selected attachment.");
      setAttachment(null);
    }
  };

  const handleCreateCategory = async () => {
    if (isSuperAdmin) return;
    if (!categoryName.trim()) {
      setError("Category name is required.");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        getApiUrl("expenses/categories"),
        {
          name: categoryName.trim(),
          description: categoryDescription.trim()
        },
        {
          ...authHeaders,
          params: getScopedParams()
        }
      );
      setMessage("Expense category created.");
      setCategoryName("");
      setCategoryDescription("");
      setIsCreatingCategory(false);
      await fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to create category.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveExpense = async (event: React.FormEvent) => {
    if (isSuperAdmin) return;
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {
        transactionNumber: formState.transactionNumber.trim() || undefined,
        receiptNumber: formState.receiptNumber.trim() || undefined,
        receiptDate: formState.receiptDate,
        vendorName: formState.vendorName.trim(),
        categoryId: formState.categoryId ? Number(formState.categoryId) : undefined,
        description: formState.description.trim(),
        amount: Number(formState.amount),
        paymentMethod: formState.paymentMethod || undefined,
        status: formState.status,
        attachment: attachment || undefined
      };

      if (formState.id) {
        await axios.put(getApiUrl(`expenses/${formState.id}`), payload, {
          ...authHeaders,
          params: getScopedParams()
        });
        setMessage("Expense updated successfully.");
      } else {
        await axios.post(getApiUrl("expenses"), payload, {
          ...authHeaders,
          params: getScopedParams()
        });
        setMessage("Expense recorded successfully.");
      }

      setIsFormOpen(false);
      resetForm();
      await Promise.all([fetchExpenses(), fetchSummary()]);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save expense.");
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveExpense = async (expenseId: number) => {
    if (isSuperAdmin) return;
    try {
      const expenseRow = expenses.find((item) => item.id === expenseId);
      await axios.delete(getApiUrl(`expenses/${expenseId}`), {
        ...authHeaders,
        params: getScopedParams(expenseRow?.tenantId)
      });
      setMessage("Expense archived successfully.");
      if (selectedExpense?.id === expenseId) {
        setIsDetailOpen(false);
        setSelectedExpense(null);
      }
      await Promise.all([fetchExpenses(), fetchSummary()]);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to archive expense.");
    }
  };

  const handleDownloadVoucher = async (expenseId: number) => {
    try {
      const expenseRow = expenses.find((item) => item.id === expenseId) || selectedExpense;
      const res = await axios.get(getApiUrl(`expenses/${expenseId}/pdf`), {
        ...authHeaders,
        params: getScopedParams(expenseRow?.tenantId),
        responseType: "blob"
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `expense_${expenseId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to download expense voucher.");
    }
  };

  const exportExcel = () => {
    if (expenses.length === 0) {
      setError("There are no expenses to export.");
      return;
    }

    const rows = expenses.map((expense) => ({
      School: expense.schoolName || (isAllSchoolsMode ? "Unassigned" : "Current School"),
      "Transaction Number": expense.transactionNumber,
      "Receipt Number": expense.receiptNumber || "",
      "Receipt Date": formatDate(expense.receiptDate),
      "Vendor Name": expense.vendorName,
      Details: expense.description,
      "Expense Category": expense.categoryName || "Other",
      Amount: Number(expense.amount || 0),
      "Payment Method": expense.paymentMethod || "",
      Status: expense.status,
      "Recorded By": expense.recordedByName || ""
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Expenses");
    XLSX.writeFile(workbook, `school_expenses_${new Date().toISOString().split("T")[0]}.xlsx`);
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank", "width=1200,height=900");
    if (!printWindow) {
      setError("Popup blocked. Allow popups to print expenses.");
      return;
    }

    const rowsHtml = expenses
      .map(
        (expense) => `
          <tr>
            <td>${expense.transactionNumber}</td>
            <td>${expense.receiptNumber || ""}</td>
            <td>${formatDate(expense.receiptDate)}</td>
            ${isSuperAdmin ? `<td>${expense.schoolName || "N/A"}</td>` : ""}
            <td>${expense.vendorName}</td>
            <td>${expense.categoryName || "Other"}</td>
            <td>${expense.description}</td>
            <td style="text-align:right;">${formatCurrency(expense.amount)}</td>
          </tr>
        `
      )
      .join("");

    const totalAmount = expenses.reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
    const categoryNameLabel =
      categories.find((item) => String(item.id) === categoryFilter)?.name || (categoryFilter ? "Selected" : "All");

    printWindow.document.write(`
      <html>
        <head>
          <title>School Expenses</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
            h1 { margin: 0 0 8px; }
            p { color: #475569; margin: 0 0 16px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #cbd5e1; padding: 10px; vertical-align: top; font-size: 12px; }
            th { background: #f8fafc; text-align: left; }
            .meta { display: flex; gap: 24px; margin-top: 12px; font-size: 13px; flex-wrap: wrap; }
            .total { margin-top: 20px; font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>School Expenses</h1>
          <p>Filtered records from ${formatDate(startDate)} to ${formatDate(endDate)}</p>
          <div class="meta">
            ${isSuperAdmin ? `<span>School: ${isAllSchoolsMode ? "All schools" : schools.find((school) => String(school.id) === selectedTenantId)?.name || "Selected school"}</span>` : ""}
            <span>Category: ${categoryNameLabel}</span>
            <span>Vendor: ${vendorFilter || "All"}</span>
            <span>Search: ${search || "None"}</span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Transaction Number</th>
                <th>Receipt Number</th>
                <th>Receipt Date</th>
                ${isSuperAdmin ? "<th>School</th>" : ""}
                <th>Seller Name</th>
                <th>Expense Category</th>
                <th>Details</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml || `<tr><td colspan="${isSuperAdmin ? 8 : 7}">No expense records found.</td></tr>`}
            </tbody>
          </table>
          <p class="total">Total: ${formatCurrency(totalAmount)}</p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const chartData = summary?.reports.monthlyTrend || [];
  const categoryBreakdown = summary?.reports.categoryBreakdown || [];
  const topVendors = summary?.reports.topVendors || [];
  const topSchools = summary?.reports.schoolBreakdown || [];
  const selectedSchoolName = selectedTenantScope
    ? schools.find((school) => school.id === selectedTenantScope)?.name || "Selected School"
    : "All Schools";
  const pageTitle = isSuperAdmin ? "School Expenses Oversight" : "School Expenses";
  const pageSubtitle = isSuperAdmin
    ? isAllSchoolsMode
      ? "Review spending across all schools, compare top spenders, and drill into a school when you need details."
      : `Review filtered spending for ${selectedSchoolName} without leaving the superadmin workspace.`
    : "Register, track, filter, export, and review school spending in one workspace.";
  const showTopSchools = isAllSchoolsMode;
  const canManageExpenses = !isSuperAdmin;

  return (
    <div className="utopia-page">
      <div className="utopia-page-header">
        <div>
          <h1 className="utopia-page-title">{pageTitle}</h1>
          <p className="page-subtitle">{pageSubtitle}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            className="utopia-btn-secondary flex items-center gap-2"
            onClick={() => document.getElementById("expense-reports")?.scrollIntoView({ behavior: "smooth" })}
          >
            <LineChartIcon size={16} /> Expense Reports
          </button>
          <button className="utopia-btn-secondary flex items-center gap-2" onClick={handlePrint}>
            <Printer size={16} /> Print
          </button>
          <button className="utopia-btn-secondary flex items-center gap-2" onClick={exportExcel}>
            <FileSpreadsheet size={16} /> Export Excel
          </button>
          {canManageExpenses && (
            <button className="utopia-btn-primary flex items-center gap-2" onClick={openCreateModal}>
              <Plus size={16} /> Register Expense
            </button>
          )}
        </div>
      </div>

      {(message || error) && (
        <div
          className={`${
            error ? "bg-red-50 border-red-100 text-red-700" : "bg-emerald-50 border-emerald-100 text-emerald-700"
          } border rounded-xl px-4 py-3 mb-6 font-medium`}
        >
          {error || message}
        </div>
      )}

      <div className="utopia-card p-5 mb-6">
        <div className="flex items-center gap-2 text-slate-600 text-sm font-bold mb-4">
          <Filter size={16} /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4">
          {isSuperAdmin && (
            <div className="xl:col-span-2">
              <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">School</label>
              <select
                className="utopia-input"
                value={selectedTenantId}
                onChange={(e) => {
                  setSelectedTenantId(e.target.value);
                  setCategoryFilter("");
                }}
              >
                <option value="all">All Schools</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="xl:col-span-3">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Search</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                className="utopia-input pl-10"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Transaction, receipt, vendor, details"
              />
            </div>
          </div>
          <div className="xl:col-span-2">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Category</label>
            <select
              className="utopia-input"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              disabled={isAllSchoolsMode}
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {isAllSchoolsMode && <p className="mt-1 text-xs text-slate-500">Select a school to filter by category or manage records.</p>}
          </div>
          <div className="xl:col-span-2">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Seller / Vendor</label>
            <input
              className="utopia-input"
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
              placeholder="Vendor name"
            />
          </div>
          <div className="xl:col-span-1">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Start Date</label>
            <input className="utopia-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div className="xl:col-span-1">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">End Date</label>
            <input className="utopia-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <div className="xl:col-span-3">
            <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Actions</label>
            <div className="flex flex-wrap items-center gap-2 xl:justify-end">
              <div className="flex items-center gap-2 whitespace-nowrap">
                <button className="utopia-btn-secondary inline-flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap" onClick={resetFilters}>
                  <CalendarRange size={14} /> Reset Filters
                </button>
                <button className="utopia-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-sm whitespace-nowrap" onClick={refreshData}>
                  <RefreshCw size={14} /> Apply Filters
                </button>
              </div>
            </div>
            <label className="mt-2 flex items-center gap-2 text-sm text-slate-600 font-medium xl:justify-end">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="rounded border-slate-300"
              />
              Include archived expenses
            </label>
          </div>
        </div>
      </div>

      {isSuperAdmin && (
        <div className="utopia-card p-4 mb-6 bg-slate-50/80 border-slate-200">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-800">
                {isAllSchoolsMode ? "Enterprise overview is active." : `Viewing ${selectedSchoolName}.`}
              </p>
              <p className="text-sm text-slate-500">
                {isAllSchoolsMode
                  ? "Use the school filter to drill into one tenant for school-specific categories and record detail."
                  : "This view stays in superadmin oversight mode while using the selected school's expense scope."}
              </p>
            </div>
            <div className="text-xs font-medium text-slate-500">
              {showTopSchools ? `${schools.length} schools available` : "School-scoped expense drill-in"}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/40">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
              <CircleDollarSign className="text-emerald-600" size={22} />
            </div>
            <div className="min-w-0">
              <h3 className="text-3xl font-bold text-slate-800 leading-none">{formatCurrency(summary?.summary.totalThisMonth || 0)}</h3>
              <p className="text-slate-400 font-medium text-sm mt-2 leading-snug">{isAllSchoolsMode ? "Total Expenses This Month Across Schools" : "Total Expenses This Month"}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/40">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center">
              <Wallet className="text-sky-600" size={22} />
            </div>
            <div className="min-w-0">
              <h3 className="text-3xl font-bold text-slate-800 leading-none">{formatCurrency(summary?.summary.totalLastThreeMonths || 0)}</h3>
              <p className="text-slate-400 font-medium text-sm mt-2 leading-snug">{isAllSchoolsMode ? "Total Expenses Last 3 Months Across Schools" : "Total Expenses Last 3 Months"}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/40">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
              <Receipt className="text-amber-600" size={22} />
            </div>
            <div className="min-w-0">
              <h3 className="text-3xl font-bold text-slate-800 leading-none">{summary?.summary.totalRecords || 0}</h3>
              <p className="text-slate-400 font-medium text-sm mt-2 leading-snug">{isAllSchoolsMode ? "Number of Expense Records Across Schools" : "Number of Expense Records"}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/40">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-violet-50 flex items-center justify-center">
              <Building2 className="text-violet-600" size={22} />
            </div>
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-slate-800 leading-tight">{summary?.summary.highestCategory || "N/A"}</h3>
              <p className="text-slate-400 font-medium text-sm mt-2 leading-snug">{isAllSchoolsMode ? "Highest Expense Category Across Schools" : "Highest Expense Category"}</p>
            </div>
          </div>
        </div>
      </div>

      <div id="expense-reports" className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-6">
          <div className="flex items-center gap-2 mb-4">
            <LineChartIcon size={18} className="text-slate-600" />
            <h2 className="text-lg font-bold text-slate-800">Expense Trend</h2>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" />
                <YAxis stroke="#64748b" />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Bar dataKey="total" radius={[10, 10, 0, 0]} fill="#0d9488" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-6">
          <div className="flex items-center gap-2 mb-4">
            <PieChartIcon size={18} className="text-slate-600" />
            <h2 className="text-lg font-bold text-slate-800">Category Breakdown</h2>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={categoryBreakdown} dataKey="total" nameKey="categoryName" innerRadius={60} outerRadius={90} paddingAngle={2}>
                  {categoryBreakdown.map((entry, index) => (
                    <Cell key={entry.categoryName} fill={REPORT_COLORS[index % REPORT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className={`grid grid-cols-1 ${showTopSchools ? "xl:grid-cols-4" : "xl:grid-cols-3"} gap-6 mb-8`}>
        <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-800">Expense Records</h2>
              <p className="text-sm text-slate-500">Showing filtered expenses for the selected period.</p>
            </div>
            {loading && (
              <div className="inline-flex items-center gap-2 text-sm text-slate-500 font-medium">
                <Loader2 size={16} className="animate-spin" /> Loading
              </div>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="utopia-table">
              <thead>
                <tr>
                  <th>Transaction Number</th>
                  <th>Receipt Number</th>
                  <th>Receipt Date</th>
                  {isSuperAdmin && <th>School</th>}
                  <th>Seller Name</th>
                  <th>Details / Description</th>
                  <th>Expense Category</th>
                  <th>Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.length === 0 && !loading && (
                  <tr>
                    <td colSpan={isSuperAdmin ? 9 : 8} className="text-center text-slate-500">
                      No expenses found for the selected filters.
                    </td>
                  </tr>
                )}
                {expenses.map((expense) => (
                  <tr key={expense.id}>
                    <td>
                      <div className="utopia-table-cell-primary">{expense.transactionNumber}</div>
                      <div className="utopia-table-cell-secondary">{expense.recordedByName || "System"}</div>
                    </td>
                    <td>{expense.receiptNumber || "N/A"}</td>
                    <td>{formatDate(expense.receiptDate)}</td>
                    {isSuperAdmin && <td>{expense.schoolName || "N/A"}</td>}
                    <td>{expense.vendorName}</td>
                    <td>
                      <div className="utopia-table-cell-primary line-clamp-2">{expense.description}</div>
                      <div className="utopia-table-cell-meta">
                        {expense.attachmentCount
                          ? `${expense.attachmentCount} attachment${expense.attachmentCount > 1 ? "s" : ""}`
                          : "No attachments"}
                      </div>
                    </td>
                    <td>{expense.categoryName || "Other"}</td>
                    <td className="font-semibold text-slate-800">{formatCurrency(expense.amount)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button
                          className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                          title="View"
                          onClick={() => openDetailModal(expense.id)}
                        >
                          <Eye size={16} />
                        </button>
                        {canManageExpenses && (
                          <button
                            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                            title="Edit"
                            onClick={() => openEditModal(expense.id)}
                          >
                            <Pencil size={16} />
                          </button>
                        )}
                        <button
                          className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
                          title="Print / Download Voucher"
                          onClick={() => handleDownloadVoucher(expense.id)}
                        >
                          <Download size={16} />
                        </button>
                        {canManageExpenses && (
                          <button
                            className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                            title="Archive"
                            onClick={() => handleArchiveExpense(expense.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {showTopSchools && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-6">
            <h2 className="text-lg font-bold text-slate-800 mb-4">Top Spending Schools</h2>
            <div className="space-y-4">
              {topSchools.length === 0 && <div className="text-sm text-slate-500">No school breakdown available for this filter set.</div>}
              {topSchools.map((school, index) => (
                <div key={`${school.tenantId}-${index}`} className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-slate-800">{school.schoolName || "Unknown School"}</p>
                      <p className="text-xs text-slate-500">Highest spending school</p>
                    </div>
                    <p className="font-bold text-slate-800">{formatCurrency(school.total)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-100 shadow-lg shadow-slate-200/30 p-6">
          <h2 className="text-lg font-bold text-slate-800 mb-4">Top Vendors</h2>
          <div className="space-y-4">
            {topVendors.length === 0 && <div className="text-sm text-slate-500">No vendor breakdown available for this filter set.</div>}
            {topVendors.map((vendor, index) => (
              <div key={`${vendor.vendorName}-${index}`} className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-slate-800">{vendor.vendorName || "Unknown Vendor"}</p>
                    <p className="text-xs text-slate-500">Top spend vendor</p>
                  </div>
                  <p className="font-bold text-slate-800">{formatCurrency(vendor.total)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isFormOpen && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsFormOpen(false)}>
          <div className="utopia-modal-content max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <div>
                <h2>{formState.id ? "Edit Expense" : "Register Expense"}</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {formState.id ? "Update the selected expense record." : "Capture a new expense for the current school."}
                </p>
              </div>
              <button className="utopia-modal-close-btn" onClick={() => setIsFormOpen(false)} aria-label="Close modal">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSaveExpense} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Transaction Number</label>
                  <input
                    className="utopia-input"
                    name="transactionNumber"
                    value={formState.transactionNumber}
                    onChange={handleFormChange}
                    placeholder="Auto-generated if left blank"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Receipt Number</label>
                  <input className="utopia-input" name="receiptNumber" value={formState.receiptNumber} onChange={handleFormChange} />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Receipt Date</label>
                  <input className="utopia-input" type="date" name="receiptDate" value={formState.receiptDate} onChange={handleFormChange} required />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Seller / Vendor Name</label>
                  <input className="utopia-input" name="vendorName" value={formState.vendorName} onChange={handleFormChange} required />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Expense Category</label>
                  <div className="flex gap-2">
                    <select className="utopia-input" name="categoryId" value={formState.categoryId} onChange={handleFormChange}>
                      <option value="">Select category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    {canManageExpenses && (
                      <button type="button" className="utopia-btn-secondary whitespace-nowrap" onClick={() => setIsCreatingCategory((prev) => !prev)}>
                        New Category
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Payment Method</label>
                  <select className="utopia-input" name="paymentMethod" value={formState.paymentMethod} onChange={handleFormChange}>
                    {PAYMENT_METHODS.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Amount</label>
                  <input className="utopia-input" type="number" min="0" step="0.01" name="amount" value={formState.amount} onChange={handleFormChange} required />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Recorded By</label>
                  <input className="utopia-input bg-slate-50" value={currentUser} readOnly />
                </div>
              </div>

              {isCreatingCategory && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-4">
                  <div className="text-sm font-semibold text-slate-700">Create Expense Category</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      className="utopia-input"
                      value={categoryName}
                      onChange={(e) => setCategoryName(e.target.value)}
                      placeholder="Category name"
                    />
                    <input
                      className="utopia-input"
                      value={categoryDescription}
                      onChange={(e) => setCategoryDescription(e.target.value)}
                      placeholder="Optional description"
                    />
                  </div>
                  <div className="flex justify-end">
                    <button type="button" className="utopia-btn-secondary" onClick={handleCreateCategory} disabled={saving}>
                      Save Category
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Description / Details</label>
                <textarea className="utopia-input min-h-[120px]" name="description" value={formState.description} onChange={handleFormChange} required />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Attachment</label>
                  <input className="utopia-input" type="file" accept="image/*,.pdf" onChange={handleAttachmentChange} />
                  {attachment && (
                    <div className="mt-2 inline-flex items-center gap-2 text-sm text-slate-600">
                      <Paperclip size={14} /> {attachment.fileName}
                    </div>
                  )}
                </div>
                {formState.id && (
                  <div>
                    <label className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1 block">Status</label>
                    <select className="utopia-input" name="status" value={formState.status} onChange={handleFormChange}>
                      <option value="active">Active</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button type="button" className="utopia-btn-secondary" onClick={() => setIsFormOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="utopia-btn-primary inline-flex items-center gap-2" disabled={saving}>
                  {saving && <Loader2 size={16} className="animate-spin" />}
                  {formState.id ? "Update Expense" : "Save Expense"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isDetailOpen && selectedExpense && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsDetailOpen(false)}>
          <div className="utopia-modal-content max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <div>
                <h2>Expense Details</h2>
                <p className="text-sm text-slate-500 mt-1">{selectedExpense.transactionNumber}</p>
              </div>
              <button className="utopia-modal-close-btn" onClick={() => setIsDetailOpen(false)} aria-label="Close modal">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Receipt Number</p>
                  <p className="font-semibold text-slate-800">{selectedExpense.receiptNumber || "N/A"}</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Receipt Date</p>
                  <p className="font-semibold text-slate-800">{formatDate(selectedExpense.receiptDate)}</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Seller / Vendor</p>
                  <p className="font-semibold text-slate-800">{selectedExpense.vendorName}</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Category</p>
                  <p className="font-semibold text-slate-800">{selectedExpense.categoryName || "Other"}</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Amount</p>
                  <p className="font-semibold text-slate-800">{formatCurrency(selectedExpense.amount)}</p>
                </div>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Recorded By</p>
                  <p className="font-semibold text-slate-800">{selectedExpense.recordedByName || "N/A"}</p>
                </div>
              </div>

              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Description / Details</p>
                <p className="text-slate-700 whitespace-pre-wrap">{selectedExpense.description}</p>
              </div>

              <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Attachments</p>
                <div className="space-y-3">
                  {(selectedExpense.attachments || []).length === 0 && (
                    <div className="text-sm text-slate-500">No attachments uploaded for this expense.</div>
                  )}
                  {(selectedExpense.attachments || []).map((item) => (
                    <div key={item.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3">
                      <div className="flex items-center gap-2 text-sm text-slate-700">
                        <Paperclip size={14} /> {item.fileName}
                      </div>
                      {item.fileData ? (
                        <a className="text-sm font-semibold text-teal-700 hover:text-teal-800" href={item.fileData} target="_blank" rel="noreferrer">
                          Open
                        </a>
                      ) : (
                        <span className="text-sm text-slate-400">Unavailable</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button className="utopia-btn-secondary inline-flex items-center gap-2" onClick={() => handleDownloadVoucher(selectedExpense.id)}>
                  <Printer size={16} /> Print Voucher
                </button>
                {canManageExpenses && (
                  <button className="utopia-btn-secondary inline-flex items-center gap-2" onClick={() => openEditModal(selectedExpense.id)}>
                    <Pencil size={16} /> Edit
                  </button>
                )}
                {canManageExpenses && (
                  <button className="utopia-btn-primary inline-flex items-center gap-2" onClick={() => handleArchiveExpense(selectedExpense.id)}>
                    <Trash2 size={16} /> Archive
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {detailLoading && (
        <div className="fixed bottom-6 right-6 bg-slate-900 text-white px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 z-50">
          <Loader2 size={16} className="animate-spin" /> Loading expense details...
        </div>
      )}
    </div>
  );
};

export default SchoolExpenses;
